import { gsap } from 'gsap';
import { getClosest, handleResponse } from '../../utils';

let forms;

const getForms = () => document.querySelectorAll('.js-form');

const resetForm = (index) => {
    forms[index].inputs.forEach((input) => {
        switch (input.type) {
            case 'text':
            case 'textarea':
            case 'email':
            case 'number':
            case 'file':
                input.value = '';
                break;

            case 'checkbox':
                input.checked = false;
                break;

            // no default
        }
    });

    forms[index].radioInputs.forEach((input) => {
        input.checked = false;
    });
};

const disableForm = (index) => {
    gsap.to(forms[index].formInner, {
        duration: 0.5,
        pointerEvents: 'none',
    });
    forms[index].loader.style.display = 'block';
};

const enableForm = (index) => {
    gsap.to(forms[index].formInner, {
        duration: 0.5,
        pointerEvents: 'auto',
    });
    forms[index].loader.style.display = 'none';
};

const notice = (index, message, reset, type = 'error') => {
    let formatMessage = message;

    if (!Array.isArray(formatMessage)) {
        formatMessage = [formatMessage];
    }

    formatMessage = formatMessage.filter((item, idx) => formatMessage.indexOf(item) === idx);

    forms[index].notice.innerHTML = `
        <ul class="mt-8 space-y-2 p-4 bg-${type} text-white text-sm list-none">
            ${formatMessage.map((line) => `<li>${line}</li>`).join('')}
        </ul>
    `;

    if (reset) resetForm(index);

    enableForm(index);

    // reset accordion height to reveal notice
    const parent = getClosest(forms[index], '.js-collapsible-content');
    if (parent) parent.style.height = 'auto';
};

const validateEmail = (input) => {
    const validRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    if (input.value.match(validRegex)) return true;
    return false;
};

const clickHandle = (event, index, form) => {
    event.preventDefault();

    disableForm(index);

    const errors = [];
    const formData = [];
    let send = true;
    let sendFile = false;
    let email = '';
    let file = [];

    forms[index].inputs.forEach((input) => {
        // inputs
        input.classList.remove('has-error');

        // radio inputs
        if (input.classList.contains('js-form-radio-input')) {
            const radioInputs = input.parentNode.querySelectorAll('.js-form-radio');
            radioInputs.forEach((radioInput) => {
                radioInput.classList.remove('has-error');
            });
        }

        // file inputs
        if (input.type === 'file') {
            getClosest(input, '.group\\/input').classList.remove('has-error');
        }

        // consent
        forms[index].formConsent.classList.remove('has-error');

        // ignore if field is hidden
        const isHidden = getClosest(input, '.js-form-select-target.hidden');
        if (isHidden) return;

        const required = input.classList.contains('is-required');
        if (required) {
            switch (input.type) {
                case 'text':
                case 'textarea':
                case 'email':
                case 'number':
                case 'hidden':
                case 'select-one':
                    if (!input.value || input.value === 0) {
                        send = false;
                        input.classList.add('has-error');

                        if (input.classList.contains('js-form-radio-input')) {
                            const radioInputs = input.parentNode.querySelectorAll('.js-form-radio');
                            radioInputs.forEach((radioInput) => {
                                radioInput.classList.add('has-error');
                            });
                        }
                    }
                    break;

                case 'checkbox':
                    if (input.checked === false) {
                        send = false;
                        input.classList.add('has-error');
                    }
                    break;

                case 'file':
                    if (input.files.length === 0
                        || !input.value
                        || input.value === 0) {
                        send = false;
                        getClosest(input, '.group\\/input').classList.add('has-error');
                    }
                    break;
                // no default
            }

            if (!send) {
                errors.push('Please complete the fields marked in red');
            }
        }

        // file
        if (input.type === 'file'
            && input.files.length > 0) {
            // validate extension
            const allowedExtensions = /(\.jpg|\.jpeg|\.png)$/i;
            if (!allowedExtensions.exec(input.files[0].name)) {
                send = false;
                errors.push('Please ensure your file type is one of the following .jpg | .jpeg | .png');
            }

            // validate size
            const fileSize = Math.round((input.files[0].size / 1024));
            if (fileSize > 10000) {
                send = false;
                errors.push('Please ensure your file size is less than 10MB');
            }

            // build file array
            [file] = input.files;
            sendFile = true;
        }

        // data consent
        if (!forms[index].formConsent.checked) {
            send = false;
            forms[index].formConsent.classList.add('has-error');
            errors.push('Please tick the consent checkbox to agree to your data being stored in line with the guidelines set out in our privacy policy');
        }

        // get primary email address
        if (input.type === 'email'
            && input.classList.contains('js-form-email')) {
            email = input.value;

            // check email is valid
            if (!validateEmail(input)) {
                send = false;
                input.classList.add('has-error');
                errors.push('Please check your email address is valid');
            }

            // check email is confirmed
            const emailConfirm = form.querySelector('.js-form-email-confirm');
            if (emailConfirm && email !== emailConfirm.value) {
                send = false;
                input.classList.add('has-error');
                errors.push('Please check your email addresses match');
            }
        }

        formData.push({
            name: input.getAttribute('name'),
            value: (input.type === 'file' ? 'Attachment' : input.value),
            required: required || false,
            label: input.dataset.label,
            key: input.dataset.key,
        });
    });

    if (!email) {
        send = false;
        errors.push('A primary email address is required');
    }

    if (!send) {
        notice(index, errors);
    } else {
        const data = new FormData();
        data.append('action', form.dataset.action);
        data.append('nonce', form.dataset.nonce);
        data.append('email', email);
        data.append('form_data', JSON.stringify(formData));

        if (sendFile) {
            data.append('file', file);
        }

        if (form.dataset.tag !== undefined) {
            data.append('tag', form.dataset.tag);
        }

        fetch(SWDLocalise.ajaxUrl, { // eslint-disable-line no-undef
            method: 'POST',
            cache: 'no-cache',
            body: data,
        })
            .then(handleResponse)
            .then((response) => {
                if (response.success) {
                    notice(index, (response.message.length > 0 ? response.message : form.dataset.successMessage), true, 'success');

                    setTimeout(() => {
                        window.location.href = '/contact/thank-you/';
                    }, 1000);
                } else {
                    notice(index, response.message);
                }
            })
            .catch((error) => {
                console.error(error); // eslint-disable-line no-console
            });
    }
};

const radioClickHandle = (input) => {
    if (input.checked) {
        const hiddenInput = input.parentNode.parentNode.parentNode.querySelector('input[type=hidden]');
        hiddenInput.value = input.value;
    }
};

const init = () => {
    forms = getForms();
    if (!forms) return;

    forms.forEach((form, i) => {
        forms[i].formInner = form.querySelector('.js-form-inner');
        forms[i].notice = form.querySelector('.js-form-notice');
        forms[i].formConsent = form.querySelector('.js-form-consent');
        forms[i].inputs = form.querySelectorAll('.js-form-input');
        forms[i].radioInputs = form.querySelectorAll('.js-form-radio');
        forms[i].btn = form.querySelector('.js-form-btn');
        forms[i].loader = form.querySelector('.js-form-loader');
        forms[i].select = form.querySelector('.js-form-select');

        if (!forms[i].btn) return;

        // prevent multiple events firing
        const newClickHandle = (event) => {
            clickHandle(event, i, form);
        };
        form.btn.addEventListener('click', newClickHandle, false);

        if (forms[i].radioInputs.length > 0) {
            forms[i].radioInputs.forEach((input) => {
                // prevent multiple events firing
                const newRadioClickHandle = () => {
                    radioClickHandle(input);
                };
                input.addEventListener('click', newRadioClickHandle, false);
            });
        }

        // select form fields
        if (forms[i].select) {
            const selectTarget = forms[i].select.value;
            const selectFields = form.querySelectorAll('.js-form-select-target');

            selectFields.forEach((field) => {
                if (field.dataset.target === selectTarget) field.classList.remove('hidden');
            });

            forms[i].select.addEventListener('change', () => {
                selectFields.forEach((field) => {
                    field.classList.add('hidden');

                    if (field.dataset.target === forms[i].select.value) {
                        field.classList.remove('hidden');
                    }
                });
            });
        }
    });
};

export default init;
